import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5l9i4mm3t2gksp8clvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#1A3D3D] text-white">
      {/* Main Footer Content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Column 1 - Logo & Description */}
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/88306fd4-3a9b-4b4d-9819-fcd38e3bf0f2.png" 
              alt="Lios S.r.l." 
              className="h-6 w-auto mb-6"
            />
            <p className="text-gray-300 text-sm font-light leading-relaxed mb-2">
              Soluzioni informatiche innovative per trasformare il vostro business. Esperienza, competenza e dedizione al servizio delle aziende.
            </p>
            <p className="text-gray-400 text-xs font-light leading-relaxed mb-4">
              Via Vittorio Emanuele III 159/161<br />
              80027 Frattamaggiore (NA)
            </p>
            <a
              href="https://public.readdy.ai/ai/img_res/88306fd4-3a9b-4b4d-9819-fcd38e3bf0f2.png"
              download="lios-srl-logo.png"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-[#FF6B35] transition-colors cursor-pointer group"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-download-2-line text-sm group-hover:text-[#FF6B35]"></i>
              </span>
              Scarica logo PNG trasparente
            </a>
          </div>

          {/* Column 2 - Newsletter */}
          <div>
            <h4 className="text-sm uppercase tracking-wider text-gray-300 mb-6 font-medium">
              NEWSLETTER
            </h4>
            <form id="newsletter_form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  required
                  disabled={isSubmitting}
                  className="w-full bg-transparent border-b border-gray-500 py-2 pr-10 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors text-sm disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center hover:text-[#FF6B35] transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-green-400 text-xs">Iscrizione completata!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-xs">Errore. Riprova.</p>
              )}
              <p className="text-xs text-gray-400 font-light">
                Iscriviti per ricevere aggiornamenti e novità.
              </p>
            </form>
          </div>

          {/* Column 3 - Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-wider text-gray-300 mb-6 font-medium">
              LINK RAPIDI
            </h4>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={() => scrollToSection('servizi')}
                  className="text-white hover:text-[#FF6B35] transition-colors text-sm font-light cursor-pointer"
                >
                  Servizi
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('chi-siamo')}
                  className="text-white hover:text-[#FF6B35] transition-colors text-sm font-light cursor-pointer"
                >
                  Chi Siamo
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contatti')}
                  className="text-white hover:text-[#FF6B35] transition-colors text-sm font-light cursor-pointer"
                >
                  Contatti
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4 - Social */}
          <div>
            <h4 className="text-sm uppercase tracking-wider text-gray-300 mb-6 font-medium">
              SEGUICI
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF6B35] transition-colors text-sm font-light cursor-pointer">
                  LinkedIn
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF6B35] transition-colors text-sm font-light cursor-pointer">
                  Facebook
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF6B35] transition-colors text-sm font-light cursor-pointer">
                  Instagram
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Large Typography */}
        <div className="relative overflow-hidden py-12">
          <h2 className="text-[120px] lg:text-[180px] font-bold text-[#F8F6F3]/10 leading-none tracking-tight">
            LIOS
          </h2>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p className="font-light">
              © 2026 Lios S.r.l. - P.IVA 08771441212 - Tutti i diritti riservati
            </p>
            <Link
              to="/privacy-policy"
              className="hover:text-[#FF6B35] transition-colors cursor-pointer font-light whitespace-nowrap"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
